import React, { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import dayjs from "dayjs";
import Popover from "@mui/material/Popover";
import { Bar, CartesianGrid, ComposedChart, LabelList, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { hubspotInsightsTargetAtom, hubspotPageAtom } from "../../state/atoms";
import { Organization } from "../../types/organizations";
import { InsightsCounters, InsightsReport } from "../../types/addons";
import { getChartRangeStats, getInsightsReport, getRangeStatsFromCache, StatsResult } from "../../services/firebase";
import {
  HsAdvancedFiltersButton,
  HsCalendarIcon,
  HsDashboardFilterBar,
  HsDashboardHeader,
  HsDashboardTitle,
  HsDataWell,
  HsDropdownButton,
  HsExportIcon,
  HsFilterPopoverHead,
  HsMenu,
  HsQuickFilter,
  HsReportCard,
  HsReportGrid,
  HsSettingsIcon,
  OuIconButton,
  OuPrimaryButton,
  OuSelect,
  OuSwitch,
  OuTable,
  OuTableColumn,
} from "../../components/HubSpotUi";
import type { TimeframeOption } from "./Insights";

/**
 * Insights for HubSpot orgs, as a HubSpot reports dashboard
 * (reports-dashboard.jpg; rpt-*.json): on the white canvas, the starred
 * dashboard title with its caret (one dashboard per report: email, profile,
 * phone, LinkedIn), Actions and the primary pill; then the quick filter row
 * (date range and frequency, Advanced filters, refresh); then the grid of
 * report cards: KPI data wells and charts drawn the way HubSpot draws them.
 * Every number the Outreach Insights page shows has a card here.
 */

type Dashboard = "email" | "profile" | "phone" | "linkedin";
type Frequency = "daily" | "weekly" | "monthly";

const DASHBOARDS: { value: Dashboard; label: string }[] = [
  { value: "email", label: "Email verification" },
  { value: "profile", label: "Profile enrichment" },
  { value: "phone", label: "Phone enrichment" },
  { value: "linkedin", label: "LinkedIn automation" },
];

const FREQUENCIES: { value: Frequency; label: string }[] = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

/* HubSpot's reporting palette. The first colour is sampled from
   reports-dashboard.jpg's bars; the rest follow HubSpot's chart order
   (UNVERIFIED: no multi-series chart was captured). */
const PALETTE = ["#fea58e", "#51d3d9", "#c9a8f5", "#f5c26b", "#ea90b1", "#9fd3a2", "#7f8fe0"];

const FAVORITE_KEY = "boring-hubspot-favorite-dashboard";

const readFavorite = (): Dashboard | null => {
  try {
    const v = localStorage.getItem(FAVORITE_KEY);
    return DASHBOARDS.some((d) => d.value === v) ? (v as Dashboard) : null;
  } catch {
    return null;
  }
};

const n = (v: number) => Math.round(v).toLocaleString("en-US");
const pct = (num: number, den: number) => (den > 0 ? `${Math.round((num / den) * 1000) / 10}%` : "0%");
const usd = (v: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(v);

/* ── data ── */

interface EmailTotals {
  prospectsChecked: number;
  valid: number;
  invalid: number;
  questionable: number;
  enrichedByBoring: number;
  bouncesPrevented: number;
}

type Row = { day: Date } & Record<string, number | Date>;

const EMPTY_EMAIL: EmailTotals = { prospectsChecked: 0, valid: 0, invalid: 0, questionable: 0, enrichedByBoring: 0, bouncesPrevented: 0 };

const fetchEmail = async (organizationId: string, start: Date, end: Date): Promise<{ totals: EmailTotals; rows: Row[] }> => {
  const range = { organizationId, startDate: dayjs(start).startOf("day"), endDate: dayjs(end).endOf("day") };
  let result: StatsResult;
  try {
    result = await getChartRangeStats(range);
  } catch {
    result = await getRangeStatsFromCache(range);
  }
  const { totals, ...days } = result;
  const rows = Object.keys(days)
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
    .map((k) => ({ day: new Date(k), ...EMPTY_EMAIL, ...(days[k] as Partial<EmailTotals>) }) as Row);
  return { totals: { ...EMPTY_EMAIL, ...(totals as Partial<EmailTotals>) }, rows };
};

/** The window just before this one, the same length, for "Compared to | Previous period". */
const previousWindow = (start: Date, end: Date): [Date, Date] => {
  const days = Math.max(1, dayjs(end).startOf("day").diff(dayjs(start).startOf("day"), "day") + 1);
  return [dayjs(start).subtract(days, "day").toDate(), dayjs(start).subtract(1, "day").endOf("day").toDate()];
};

/** Sums daily rows into the chosen frequency's buckets, labelled the way HubSpot labels its x axis. */
const bucket = (rows: Row[], keys: string[], freq: Frequency): Record<string, number | string>[] => {
  const out = new Map<string, Record<string, number | string>>();
  for (const r of rows) {
    const d = dayjs(r.day);
    const start = freq === "monthly" ? d.startOf("month") : freq === "weekly" ? d.subtract((d.day() + 6) % 7, "day").startOf("day") : d.startOf("day");
    const id = start.format("YYYY-MM-DD");
    if (!out.has(id)) {
      out.set(id, {
        label: freq === "monthly" ? start.format("MMM YYYY") : freq === "weekly" ? `Week of ${start.format("MMM D")}` : start.format("MMM D"),
        ...Object.fromEntries(keys.map((k) => [k, 0])),
      });
    }
    const b = out.get(id)!;
    for (const k of keys) b[k] = (b[k] as number) + Number(r[k] || 0);
  }
  return Array.from(out.values());
};

/** "▲ 12%" against the previous period, or nothing when there is nothing to compare with. */
const change = (cur: number, prev: number | undefined, higherIsGood = true) => {
  if (prev === undefined) return null;
  if (prev === 0) return cur === 0 ? { text: "0%", direction: "flat" as const } : null;
  const d = ((cur - prev) / prev) * 100;
  const direction = Math.abs(d) < 0.05 ? ("flat" as const) : d > 0 ? ("up" as const) : ("down" as const);
  return { text: `${Math.abs(Math.round(d * 10) / 10)}%`, direction, good: direction === "flat" ? undefined : (direction === "up") === higherIsGood };
};

/* ── charts, in HubSpot's reporting style ── */

interface Series {
  key: string;
  label: string;
  format?: (v: number) => string;
}

const ChartLegend: FC<{ series: Series[] }> = ({ series }) => (
  <ul className="hs-chart-legend">
    {series.map((s, i) => (
      <li key={s.key}>
        <span className="hs-chart-legend__dot" style={{ background: PALETTE[i % PALETTE.length] }} />
        {s.label}
      </li>
    ))}
  </ul>
);

const ChartTooltip: FC<{ active?: boolean; label?: string; payload?: { name: string; value: number; color: string; dataKey: string }[]; series: Series[] }> = ({
  active,
  label,
  payload,
  series,
}) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="hs-chart-tooltip">
      <strong>{label}</strong>
      {payload.map((p) => {
        const s = series.find((x) => x.key === p.dataKey);
        return (
          <div key={p.dataKey}>
            <span style={{ color: p.color }}>●</span> {s?.label ?? p.name}: <b>{s?.format ? s.format(p.value) : n(p.value)}</b>
          </div>
        );
      })}
    </div>
  );
};

const AXIS_TICK = { fill: "#333333", fontSize: 11, fontWeight: 300, fontFamily: "Lexend Deca, Helvetica, Arial, sans-serif" };
const AXIS_TITLE = { fill: "#333333", fontSize: 12, fontWeight: 600, fontFamily: "Lexend Deca, Helvetica, Arial, sans-serif" };

/** A time series: lines with hollow markers, columns, or stacked columns; dashed grid, titled axes. */
const TimeChart: FC<{ data: Record<string, number | string>[]; series: Series[]; kind?: "line" | "column" | "stacked"; yTitle: string; xTitle?: string; compact?: boolean }> = ({
  data,
  series,
  kind = "line",
  yTitle,
  xTitle,
  compact,
}) => (
  <div className="hs-chart">
    {!compact && <ChartLegend series={series} />}
    <div style={{ position: "absolute", inset: compact ? 0 : "26px 0 0 0" }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 16, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke="#cccccc" strokeDasharray="4 3" />
          <XAxis
            dataKey="label"
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: "#cccccc" }}
            minTickGap={16}
            label={xTitle && !compact ? { value: xTitle, position: "insideBottom", offset: 0, style: AXIS_TITLE } : undefined}
            height={xTitle && !compact ? 44 : 24}
          />
          <YAxis
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
            width={compact ? 32 : 56}
            tickFormatter={(v: number) => (series[0]?.format ? series[0].format(v) : n(v))}
            label={compact ? undefined : { value: yTitle, angle: -90, position: "insideLeft", offset: 10, style: { ...AXIS_TITLE, textAnchor: "middle" } }}
          />
          <Tooltip content={<ChartTooltip series={series} />} cursor={{ fill: "rgba(20, 20, 20, 0.04)" }} />
          {series.map((s, i) =>
            kind === "line" ? (
              <Line
                key={s.key}
                type="linear"
                dataKey={s.key}
                name={s.label}
                stroke={PALETTE[i % PALETTE.length]}
                strokeWidth={2}
                dot={{ r: 3, fill: "#ffffff", stroke: PALETTE[i % PALETTE.length], strokeWidth: 2 }}
                activeDot={{ r: 4 }}
              />
            ) : (
              <Bar key={s.key} dataKey={s.key} name={s.label} fill={PALETTE[i % PALETTE.length]} stackId={kind === "stacked" ? "a" : undefined} maxBarSize={56} />
            )
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  </div>
);

/** One series by category (HubSpot's "Contact Sources"): coral columns with their values on top. */
const CategoryBars: FC<{ data: { label: string; value: number }[]; seriesLabel: string; yTitle: string; xTitle: string }> = ({ data, seriesLabel, yTitle, xTitle }) => (
  <div className="hs-chart">
    <ChartLegend series={[{ key: "value", label: seriesLabel }]} />
    <div style={{ position: "absolute", inset: "26px 0 0 0" }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 20, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke="#cccccc" strokeDasharray="4 3" />
          <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: "#cccccc" }} label={{ value: xTitle, position: "insideBottom", offset: 0, style: AXIS_TITLE }} height={44} />
          <YAxis
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
            width={56}
            tickFormatter={n}
            label={{ value: yTitle, angle: -90, position: "insideLeft", offset: 10, style: { ...AXIS_TITLE, textAnchor: "middle" } }}
          />
          <Tooltip content={<ChartTooltip series={[{ key: "value", label: seriesLabel }]} />} cursor={{ fill: "rgba(20, 20, 20, 0.04)" }} />
          <Bar dataKey="value" name={seriesLabel} fill={PALETTE[0]} maxBarSize={280}>
            <LabelList dataKey="value" position="top" formatter={(v: number) => n(v)} style={{ fill: "#333333", fontSize: 11, fontWeight: 600, fontFamily: "Lexend Deca, Helvetica, Arial, sans-serif" }} />
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  </div>
);

/* ── the dashboards' activity reports (the same metrics as ActivityPanels) ── */

type CounterKey = keyof InsightsCounters | "phoneCostUsd";

interface ActivityKpi {
  label: string;
  value: (t: InsightsCounters, r: InsightsReport) => number;
  format?: (v: number, t: InsightsCounters, r: InsightsReport) => string;
  /** A line under the number, e.g. the share it is of another count. */
  note?: (v: number, t: InsightsCounters, r: InsightsReport) => string;
}

interface ActivitySection {
  title: string;
  info: string;
  kpis: ActivityKpi[];
  chartTitle: string;
  series: (Series & { key: CounterKey })[];
}

const ACTIVITY: Record<Exclude<Dashboard, "email">, { sections: ActivitySection[]; userColumns: { key: CounterKey; label: string; money?: boolean }[] }> = {
  profile: {
    sections: [
      {
        title: "Profile enrichment",
        info: "LinkedIn profiles boring read for your contacts, and the ones where it changed something.",
        kpis: [
          { label: "Profiles checked", value: (t) => t.profileRefreshes },
          { label: "Profiles updated", value: (t) => t.profileUpdates || 0 },
          { label: "Fields changed", value: (t) => t.profileFieldsUpdated },
          { label: "Job changes found", value: (t) => t.jobChangesDetected },
        ],
        chartTitle: "Profile enrichment over time",
        series: [
          { key: "profileRefreshes", label: "Profiles checked" },
          { key: "profileUpdates", label: "Profiles updated" },
          { key: "profileFieldsUpdated", label: "Fields changed" },
          { key: "jobChangesDetected", label: "Job changes" },
        ],
      },
      {
        title: "LinkedIn exports",
        info: "Profiles your team exported from LinkedIn, and the emails boring found for them.",
        kpis: [
          { label: "Profiles exported", value: (t) => t.exportRequests },
          { label: "Emails found", value: (t) => t.exportEmailsFound, note: (v, t) => `${pct(v, t.exportRequests)} of exports` },
          { label: "Enrich requests", value: (t) => t.extensionEnrichRequests },
        ],
        chartTitle: "LinkedIn exports over time",
        series: [
          { key: "exportRequests", label: "Exports" },
          { key: "exportEmailsFound", label: "Emails found" },
          { key: "extensionEnrichRequests", label: "Enrich requests" },
        ],
      },
    ],
    userColumns: [
      { key: "profileRefreshes", label: "Profiles checked" },
      { key: "profileUpdates", label: "Profiles updated" },
      { key: "profileFieldsUpdated", label: "Fields changed" },
      { key: "jobChangesDetected", label: "Job changes" },
      { key: "exportRequests", label: "Exports" },
      { key: "exportEmailsFound", label: "Emails found" },
    ],
  },
  phone: {
    sections: [
      {
        title: "Phone enrichment",
        info: "Numbers boring looked up, the ones it found, and what they cost.",
        kpis: [
          { label: "Lookups", value: (t) => t.phoneLookups },
          { label: "Numbers found", value: (t) => t.phoneLookupsFound, note: (v, t) => `${pct(v, t.phoneLookups)} of lookups` },
          { label: "Manual lookups", value: (t) => t.phoneManualLookups },
          { label: "Spend", value: (t) => t.phoneCostUsdMicros / 1_000_000, format: (v) => usd(v) },
        ],
        chartTitle: "Phone enrichment over time",
        series: [
          { key: "phoneLookups", label: "Lookups" },
          { key: "phoneLookupsFound", label: "Numbers found" },
          { key: "phoneManualLookups", label: "Manual" },
        ],
      },
      {
        title: "Phone verification",
        info: "Numbers boring checked before your team called them.",
        kpis: [{ label: "Numbers verified", value: (t) => t.phoneVerifications }],
        chartTitle: "Phone spend over time",
        series: [{ key: "phoneCostUsd", label: "Spend", format: usd }],
      },
    ],
    userColumns: [
      { key: "phoneLookups", label: "Lookups" },
      { key: "phoneLookupsFound", label: "Numbers found" },
      { key: "phoneManualLookups", label: "Manual" },
      { key: "phoneVerifications", label: "Verifications" },
      { key: "phoneCostUsd", label: "Spend", money: true },
    ],
  },
  linkedin: {
    sections: [
      {
        title: "LinkedIn automation",
        info: "Connection requests and messages boring sent for your team on LinkedIn.",
        kpis: [
          { label: "Connects sent", value: (t) => t.linkedinConnectsSent },
          { label: "Accepted", value: (t) => t.linkedinConnectsAccepted, note: (v, t) => `${pct(v, t.linkedinConnectsSent)} of connects` },
          { label: "Messages sent", value: (t) => t.linkedinMessagesSent },
          { label: "Replies", value: (_t, r) => r.linkedin.replies, note: (v, t) => `${pct(v, t.linkedinMessagesSent)} of messages` },
        ],
        chartTitle: "LinkedIn automation over time",
        series: [
          { key: "linkedinConnectsSent", label: "Connects sent" },
          { key: "linkedinConnectsAccepted", label: "Accepted" },
          { key: "linkedinMessagesSent", label: "Messages" },
          { key: "linkedinProfileViews", label: "Profile views" },
        ],
      },
      {
        title: "Automation queue",
        info: "LinkedIn actions waiting, done and failed.",
        kpis: [
          { label: "Queued", value: (_t, r) => r.linkedin.queue?.pending || 0 },
          { label: "Completed", value: (_t, r) => r.linkedin.queue?.completed || 0 },
          { label: "Failed", value: (_t, r) => r.linkedin.queue?.failed || 0 },
        ],
        chartTitle: "Messages and replies",
        series: [
          { key: "linkedinMessagesSent", label: "Messages" },
          { key: "linkedinReplies", label: "Replies" },
        ],
      },
    ],
    userColumns: [
      { key: "linkedinConnectsSent", label: "Connects sent" },
      { key: "linkedinConnectsAccepted", label: "Accepted" },
      { key: "linkedinMessagesSent", label: "Messages" },
      { key: "linkedinProfileViews", label: "Profile views" },
    ],
  },
};

/** Every dashboard and the cards on it, by title, for the top bar's search. */
export const HUBSPOT_REPORTS: { dashboard: Dashboard; label: string; cards: string[] }[] = DASHBOARDS.map((d) => ({
  dashboard: d.value,
  label: d.label,
  cards:
    d.value === "email"
      ? ["Prospects verified", "Email verdicts", "Email data accuracy", "Prospects verified over time", "boring ROI", "Bounces prevented over time", "Email verdicts over time"]
      : [...ACTIVITY[d.value].sections.flatMap((s) => [s.title, s.chartTitle]), "By user"],
}));

/* ── the page ── */

interface Props {
  organizationId?: string;
  organization: Organization | null;
  timeframeOptions: TimeframeOption[];
}

const HubSpotInsights: FC<Props> = ({ organizationId, organization, timeframeOptions }) => {
  const setPage = useSetRecoilState(hubspotPageAtom);
  const [dashboard, setDashboard] = useState<Dashboard>(() => readFavorite() ?? "email");
  const [favorite, setFavorite] = useState<Dashboard | null>(readFavorite);
  const [timeframe, setTimeframe] = useState<TimeframeOption>(timeframeOptions[0]);
  const [frequency, setFrequency] = useState<Frequency>("daily");
  const [compare, setCompare] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  // A report picked in the top bar's search: open its dashboard, then once
  // it has loaded, bring the card into view and ring it for a moment.
  const [target, setTarget] = useRecoilState(hubspotInsightsTargetAtom);
  const [pendingCard, setPendingCard] = useState<string | null>(null);
  useEffect(() => {
    if (!target) return;
    if (DASHBOARDS.some((d) => d.value === target.dashboard)) setDashboard(target.dashboard as Dashboard);
    setPendingCard(target.card ?? null);
    setTarget(null);
  }, [target, setTarget]);

  const [titleAnchor, setTitleAnchor] = useState<HTMLElement | null>(null);
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
  const [dateAnchor, setDateAnchor] = useState<HTMLElement | null>(null);
  const [freqAnchor, setFreqAnchor] = useState<HTMLElement | null>(null);
  const [advAnchor, setAdvAnchor] = useState<HTMLElement | null>(null);
  const [draftRange, setDraftRange] = useState(timeframe.value);
  const [draftFreq, setDraftFreq] = useState<Frequency>(frequency);

  const [email, setEmail] = useState<{ totals: EmailTotals; rows: Row[] } | null>(null);
  const [emailPrev, setEmailPrev] = useState<EmailTotals | null>(null);
  const [report, setReport] = useState<InsightsReport | null>(null);
  const [reportPrev, setReportPrev] = useState<InsightsCounters | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;
    const { startDate, endDate } = timeframe;
    const [prevStart, prevEnd] = previousWindow(startDate, endDate);
    setLoading(true);
    setError(null);
    const jobs: Promise<unknown>[] = [];
    if (dashboard === "email") {
      jobs.push(fetchEmail(organizationId, startDate, endDate).then((r) => !cancelled && setEmail(r)));
      if (compare) jobs.push(fetchEmail(organizationId, prevStart, prevEnd).then((r) => !cancelled && setEmailPrev(r.totals)).catch(() => !cancelled && setEmailPrev(null)));
    } else {
      jobs.push(getInsightsReport(startDate, endDate).then((r) => !cancelled && setReport(r)));
      if (compare) jobs.push(getInsightsReport(prevStart, prevEnd).then((r) => !cancelled && setReportPrev(r.totals)).catch(() => !cancelled && setReportPrev(null)));
    }
    Promise.all(jobs)
      .catch(() => !cancelled && setError("Some reports couldn't load. Refresh to try again."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [organizationId, dashboard, timeframe, compare, refreshKey]);

  useEffect(() => {
    if (!pendingCard || loading) return;
    const timer = window.setTimeout(() => {
      setPendingCard(null);
      const el = Array.from(document.querySelectorAll<HTMLElement>("[data-hs-card]")).find((e) => e.dataset.hsCard === pendingCard);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("is-search-hit");
      window.setTimeout(() => el.classList.remove("is-search-hit"), 1600);
    }, 50);
    return () => window.clearTimeout(timer);
  }, [pendingCard, loading]);

  const freqLabel = FREQUENCIES.find((f) => f.value === frequency)?.label ?? "Daily";
  const rangeChip = timeframe.label;
  const seriesChip = `${timeframe.label} | ${freqLabel}`;
  const compareChip = compare ? "Compared to | Previous period" : null;
  const kpiChips = [rangeChip, ...(compareChip ? [compareChip] : [])];
  const dashLabel = DASHBOARDS.find((d) => d.value === dashboard)?.label ?? "";

  const exportCsv = () => {
    let header: string[] = [];
    let lines: (string | number)[][] = [];
    if (dashboard === "email" && email) {
      header = ["Date", "Prospects verified", "Valid", "Invalid", "Questionable", "Bounces prevented", "Emails enriched"];
      lines = email.rows.map((r) => [dayjs(r.day).format("YYYY-MM-DD"), r.prospectsChecked as number, r.valid as number, r.invalid as number, r.questionable as number, r.bouncesPrevented as number, r.enrichedByBoring as number]);
    } else if (report) {
      const cols = ACTIVITY[dashboard as Exclude<Dashboard, "email">].sections.flatMap((s) => s.series);
      header = ["Date", ...cols.map((c) => c.label)];
      lines = report.days.map((d) => [d.date, ...cols.map((c) => (c.key === "phoneCostUsd" ? d.phoneCostUsdMicros / 1_000_000 : Number(d[c.key as keyof InsightsCounters] || 0)))]);
    }
    const csv = [header, ...lines].map((l) => l.join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `boring-${dashboard}-${dayjs(timeframe.startDate).format("YYYY-MM-DD")}-${dayjs(timeframe.endDate).format("YYYY-MM-DD")}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const menu = (anchor: HTMLElement | null, close: () => void, items: { label: ReactNode; run: () => void; selected?: boolean }[], align: "left" | "right" = "left") => (
    <HsMenu
      open={!!anchor}
      anchorEl={anchor}
      onClose={close}
      anchorOrigin={{ vertical: "bottom", horizontal: align }}
      transformOrigin={{ vertical: "top", horizontal: align }}
      PaperProps={{ style: { marginTop: 4, minWidth: 220 } }}
    >
      {items.map((it, i) => (
        <li key={i} role="none">
          <button
            type="button"
            role="menuitem"
            className={`hs-menu__item${it.selected ? " is-selected" : ""}`}
            onClick={() => {
              close();
              it.run();
            }}
          >
            {it.label}
          </button>
        </li>
      ))}
    </HsMenu>
  );

  const filterPopover = (anchor: HTMLElement | null, close: () => void, body: ReactNode) => (
    <Popover
      open={!!anchor}
      anchorEl={anchor}
      onClose={close}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{ className: "hs-popover hs-mui-popover", elevation: 0, style: { marginTop: 4 } }}
    >
      <div className="hs-filter-pop">{body}</div>
    </Popover>
  );

  /* ── cards ── */

  const loadingValue = loading ? "–" : null;

  const emailCards = () => {
    const t = email?.totals ?? EMPTY_EMAIL;
    const rows = email?.rows ?? [];
    const accuracy = (k: "valid" | "invalid" | "questionable") => pct(t[k], t.valid + t.invalid + t.questionable);
    const enrichmentOn = organization?.isEnrichmentEnabled ?? false;
    return (
      <>
        <HsReportCard title="Prospects verified" info="Contacts boring checked an email for." chips={kpiChips}>
          <HsDataWell
            items={[
              {
                label: "(Count) Contacts verified",
                value: loadingValue ?? n(t.prospectsChecked),
                change: compareChip && !loading ? change(t.prospectsChecked, emailPrev?.prospectsChecked) : null,
              },
            ]}
          />
        </HsReportCard>
        <HsReportCard title="Email verdicts" info="How the emails boring checked came out." chips={[rangeChip]} span={2} rows={2}>
          {rows.length === 0 && !loading ? (
            <div className="hs-chart-empty">No emails checked in this period.</div>
          ) : (
            <CategoryBars
              seriesLabel="(Count) Contacts"
              yTitle="(Count) Contacts"
              xTitle="Email verdict"
              data={[
                { label: "Valid", value: t.valid },
                { label: "Questionable", value: t.questionable },
                { label: "Invalid", value: t.invalid },
              ]}
            />
          )}
        </HsReportCard>
        <HsReportCard title="Email data accuracy" info="The share of checked emails with each verdict." chips={[rangeChip]}>
          <HsDataWell
            items={[
              { label: "Valid", value: loadingValue ?? accuracy("valid") },
              { label: "Invalid", value: loadingValue ?? accuracy("invalid") },
              { label: "Questionable", value: loadingValue ?? accuracy("questionable") },
            ]}
          />
        </HsReportCard>
        <HsReportCard title="Prospects verified over time" info="Contacts boring checked an email for, by day." chips={[seriesChip]} span={2} rows={2}>
          <TimeChart data={bucket(rows, ["prospectsChecked"], frequency)} series={[{ key: "prospectsChecked", label: "(Count) Contacts verified" }]} yTitle="(Count) Contacts" xTitle="Verified date" />
        </HsReportCard>
        <HsReportCard title="boring ROI" info="Bounces boring stopped before they happened, and emails it found." chips={kpiChips}>
          <HsDataWell
            items={[
              {
                label: "Bounces prevented",
                value: loadingValue ?? n(t.bouncesPrevented),
                change: compareChip && !loading ? change(t.bouncesPrevented, emailPrev?.bouncesPrevented) : null,
              },
              enrichmentOn
                ? {
                    label: "Emails enriched",
                    value: loadingValue ?? n(t.enrichedByBoring),
                    change: compareChip && !loading ? change(t.enrichedByBoring, emailPrev?.enrichedByBoring) : null,
                  }
                : {
                    label: "Emails enriched",
                    value: (
                      <button type="button" className="hs-btn hs-btn--primary hs-focusable" style={{ marginTop: 2 }} onClick={() => setPage("addons")}>
                        Upgrade
                      </button>
                    ),
                  },
            ]}
          />
        </HsReportCard>
        <HsReportCard title="Bounces prevented over time" info="Invalid emails boring kept your team from sending to." chips={[seriesChip]}>
          <TimeChart compact data={bucket(rows, ["bouncesPrevented"], frequency)} series={[{ key: "bouncesPrevented", label: "Bounces prevented" }]} yTitle="(Count) Bounces" />
        </HsReportCard>
        <HsReportCard title="Email verdicts over time" info="Valid, invalid and questionable emails, by day." chips={[seriesChip]} span={3} rows={2}>
          <TimeChart
            kind="stacked"
            data={bucket(rows, ["valid", "questionable", "invalid", "enrichedByBoring"], frequency)}
            series={[
              { key: "valid", label: "Valid" },
              { key: "questionable", label: "Questionable" },
              { key: "invalid", label: "Invalid" },
              ...(enrichmentOn ? [{ key: "enrichedByBoring", label: "Enriched by boring" }] : []),
            ]}
            yTitle="(Count) Contacts"
            xTitle="Verified date"
          />
        </HsReportCard>
      </>
    );
  };

  const activityCards = (key: Exclude<Dashboard, "email">) => {
    const def = ACTIVITY[key];
    const t = report?.totals;
    const rows: Row[] = (report?.days ?? []).map((d) => ({ ...d, day: new Date(`${d.date}T12:00:00`), phoneCostUsd: d.phoneCostUsdMicros / 1_000_000 }) as unknown as Row);
    const users = (report?.byUser ?? [])
      .map((u) => ({ ...u, phoneCostUsd: u.phoneCostUsdMicros / 1_000_000 }))
      .filter((u) => def.userColumns.some((c) => Number(u[c.key as keyof typeof u] || 0) > 0));
    const userColumns: OuTableColumn<(typeof users)[number]>[] = [
      { key: "user", header: "User", width: 260, sticky: true, render: (u) => <span className="hs-user-cell__name">{u.user}</span> },
      ...def.userColumns.map((c) => ({
        key: c.key,
        header: c.label,
        width: 160,
        render: (u: (typeof users)[number]) => {
          const v = Number(u[c.key as keyof typeof u] || 0);
          return c.money ? usd(v) : n(v);
        },
      })),
    ];
    return (
      <>
        {def.sections.map((s, i) => (
          <HsReportCard key={`kpi-${s.title}`} title={s.title} info={s.info} chips={kpiChips} span={s.kpis.length > 3 ? 2 : 1} className={i === 0 ? undefined : undefined}>
            <HsDataWell
              items={s.kpis.map((k) => {
                const v = t && report ? k.value(t, report) : 0;
                const prev = reportPrev && report ? k.value(reportPrev, { ...report, totals: reportPrev }) : undefined;
                return {
                  label: k.label,
                  value: loadingValue ?? (t && report && k.format ? k.format(v, t, report) : n(v)),
                  note: !loading && t && report && k.note ? k.note(v, t, report) : undefined,
                  change: compareChip && !loading && s.title !== "Automation queue" ? change(v, prev) : null,
                };
              })}
            />
          </HsReportCard>
        ))}
        {def.sections.map((s, i) => (
          <HsReportCard key={`chart-${s.title}`} title={s.chartTitle} info={s.info} chips={[seriesChip]} span={i === 0 ? 2 : 1} rows={2}>
            <TimeChart
              data={bucket(rows, s.series.map((x) => x.key), frequency)}
              series={s.series}
              kind={s.series.length > 1 ? "line" : "column"}
              yTitle={s.series[0].format ? "Spend (USD)" : "(Count)"}
              xTitle={s.series.length > 1 ? "Date" : undefined}
            />
          </HsReportCard>
        ))}
        <HsReportCard title="By user" info="Each person's activity in this period." chips={[rangeChip]} span={3} rows={users.length > 6 ? 3 : 2}>
          {users.length === 0 && !loading ? (
            <div className="hs-chart-empty">No activity from anyone in this period yet.</div>
          ) : (
            <div className="hs-report-card__table">
              <OuTable columns={userColumns} rows={users} getRowId={(u) => u.user} loading={loading} skeletonRows={4} pageSize={50} />
            </div>
          )}
        </HsReportCard>
      </>
    );
  };

  return (
    <main className="hs-canvas" aria-label="Insights">
      <HsDashboardHeader
        title={
          <HsDashboardTitle
            title={dashLabel}
            starred={favorite === dashboard}
            open={!!titleAnchor}
            onOpen={setTitleAnchor}
            onStar={() => {
              const next = favorite === dashboard ? null : dashboard;
              setFavorite(next);
              try {
                if (next) localStorage.setItem(FAVORITE_KEY, next);
                else localStorage.removeItem(FAVORITE_KEY);
              } catch {
                /* the favourite just won't stick */
              }
            }}
          />
        }
        actions={
          <>
            <HsDropdownButton use="secondary" onClick={(e) => setActionsAnchor(e.currentTarget)} aria-expanded={!!actionsAnchor}>
              Actions
            </HsDropdownButton>
            <OuPrimaryButton size="small" onClick={exportCsv} disabled={loading} endIcon={<HsExportIcon />}>
              Export
            </OuPrimaryButton>
            <OuIconButton aria-label="Dashboard settings" title="Settings" onClick={() => setPage("workflows")}>
              <HsSettingsIcon />
            </OuIconButton>
          </>
        }
      />
      {menu(
        titleAnchor,
        () => setTitleAnchor(null),
        DASHBOARDS.map((d) => ({ label: d.label + (d.value === favorite ? " ★" : ""), selected: d.value === dashboard, run: () => setDashboard(d.value) }))
      )}
      {menu(
        actionsAnchor,
        () => setActionsAnchor(null),
        [
          { label: "Refresh data", run: () => setRefreshKey((k) => k + 1) },
          { label: "Export as CSV", run: exportCsv },
          { label: compare ? "Stop comparing to the previous period" : "Compare to the previous period", run: () => setCompare((c) => !c) },
          { label: "Open settings", run: () => setPage("account") },
        ],
        "right"
      )}

      <HsDashboardFilterBar
        quickFilters={
          <>
            <HsQuickFilter
              label="Date range"
              active={timeframe.value !== timeframeOptions[0].value}
              open={!!dateAnchor}
              data-hs-quick-filter="daterange"
              onOpen={(el) => {
                setDraftRange(timeframe.value);
                setDateAnchor(el);
              }}
            />
            <HsQuickFilter
              label="Frequency"
              active={frequency !== "daily"}
              open={!!freqAnchor}
              data-hs-quick-filter="frequency"
              onOpen={(el) => {
                setDraftFreq(frequency);
                setFreqAnchor(el);
              }}
            />
          </>
        }
        advanced={<HsAdvancedFiltersButton expanded={!!advAnchor} count={compare ? 1 : 0} onClick={setAdvAnchor} />}
        onRefresh={() => setRefreshKey((k) => k + 1)}
        refreshing={loading}
      />

      {filterPopover(
        dateAnchor,
        () => setDateAnchor(null),
        <>
          <HsFilterPopoverHead
            title="Date range"
            type="Datetime"
            typeIcon={<HsCalendarIcon />}
            applyDisabled={draftRange === timeframe.value}
            onApply={() => {
              const next = timeframeOptions.find((o) => o.value === draftRange);
              if (next) setTimeframe(next);
              setDateAnchor(null);
            }}
          />
          <div className="hs-filter-pop__body">
            <p className="hs-filter-pop__hint">This is a dynamic quick filter that filters the dashboard based on date range.</p>
            <OuSelect value={draftRange} onChange={setDraftRange} options={timeframeOptions.map((o) => ({ value: o.value, label: o.label }))} aria-label="Date range" />
          </div>
        </>
      )}
      {filterPopover(
        freqAnchor,
        () => setFreqAnchor(null),
        <>
          <HsFilterPopoverHead
            title="Frequency"
            type="How charts over time are grouped"
            applyDisabled={draftFreq === frequency}
            onApply={() => {
              setFrequency(draftFreq);
              setFreqAnchor(null);
            }}
          />
          <div className="hs-filter-pop__body">
            <OuSelect value={draftFreq} onChange={(v) => setDraftFreq(v as Frequency)} options={FREQUENCIES} aria-label="Frequency" />
          </div>
        </>
      )}
      {filterPopover(
        advAnchor,
        () => setAdvAnchor(null),
        <>
          <div className="hs-filter-pop__title-row">
            <h5 className="hs-filter-pop__title">Advanced filters</h5>
          </div>
          <div className="hs-filter-pop__body">
            <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
              <span>Compare to the previous period</span>
              <OuSwitch checked={compare} onChange={setCompare} aria-label="Compare to the previous period" />
            </label>
            <p className="hs-filter-pop__hint" style={{ color: "#666666", fontSize: 12, lineHeight: "18px" }}>
              KPIs show the change from the same number of days just before {timeframe.label.toLowerCase()}.
            </p>
          </div>
        </>
      )}

      {error && (
        <div className="hs-alert" data-hs-tone="danger" role="alert" style={{ margin: "0 24px 16px" }}>
          <span>{error}</span>
        </div>
      )}
      <HsReportGrid>{dashboard === "email" ? emailCards() : activityCards(dashboard)}</HsReportGrid>
    </main>
  );
};

export default HubSpotInsights;
